import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs/operators';
import { RackeoLocaleService } from '../shared/services/locale.service';
import { MetaService } from '@ngx-meta/core';
import { CanonicalService } from '../shared/services/canonical.service';
import { RACKEO_SITE } from '../shared/rackeo-site';
import { RackeoCartService } from '../shared/services/cart.service';
import type {
  RackeoContent,
  RackeoMenuPromoData,
  RackeoMenuVpsPriceRow,
  RackeoVirtualizacionAddOn,
  RackeoVirtualizacionContract,
  RackeoVirtualizacionData,
  RackeoVirtualizacionPlan,
  RackeoVirtualizacionPriceRow,
  RackeoVpsSpec
} from '../shared/services/locale.service';
import { scrollToElementById } from '../../../../common/functions/scroll-functions';

export interface VpsComparisonRow {
  section?: string;
  feature?: string;
  helpText?: string;
  cells?: string[];
}

const HERO_DEFAULT_SRC = 'assets/images/rackeo/virtualizacion/hero/banner-vps.png';
const HERO_DEFAULT_ALT = 'VPS Cloud SSD Rackeo — Virtualización Linux con los precios más bajos del mercado';
const VPS_PLAN_IMAGE_URL = 'assets/images/rackeo/virtualizacion/hero/vps-plan-ssd.jpg';

const PLAN_TAB_ACCENTS = ['#f26532', '#f47d31', '#f68933', '#f8981d', '#fdba31', '#fdba31', '#fdba31'];

const PRICE_STRIP_COLORS: string[][] = [
  ['#f37344', '#f48157', '#f68a63', '#f79a68', '#f79a68'],
  ['#f58944', '#f69456', '#f79c63', '#f8a977', '#f8a977'],
  ['#f79445', '#f89e58', '#f8a564', '#f9b179', '#f9b179'],
  ['#f9a131', '#f9ab46', '#fab153', '#fabb6a', '#fabb6a'],
  ['#fdc044', '#fdc656', '#fdcb62', '#fed277', '#fed277'],
  ['#fdc044', '#fdc656', '#fdcb62', '#fed277', '#fed277'],
  ['#fdc044', '#fdc656', '#fdcb62', '#fed277', '#fed277']
];

const CMP_HEAD_BG = ['#d56537', '#da7c38', '#dd883c', '#dd883c', '#dd883c', '#e1962f', '#ecb940'];

@Component({
  selector: 'app-virtualizacion',
  templateUrl: './virtualizacion.component.html',
  styleUrls: ['./virtualizacion.component.scss']
})
export class VirtualizacionComponent implements OnInit {
  activeTab = 0;
  selectedPlanIndex = 0;

  readonly tabPills = ['Generales', 'Caracteristicas', 'Comparar Planes', 'Adicionales'];
  readonly tabSectionTitles = ['GENERAL', 'CARACTERISTICAS', 'COMPARAR PLANES', 'ADICIONALES'];

  heroSrc = HERO_DEFAULT_SRC;
  heroAlt = HERO_DEFAULT_ALT;
  plans: RackeoVirtualizacionPlan[] = [];
  comparisonRows: VpsComparisonRow[] = [];
  comparisonPriceRows: RackeoVirtualizacionPriceRow[] = [];
  addOns: RackeoVirtualizacionAddOn[] = [];

  private vpsPids: number[] = [];
  private ramLabels: string[] = [];
  private specs: RackeoVpsSpec[] = [];
  private fallbackMonthlyPrices: number[] = [];

  readonly cmpHeadBg = CMP_HEAD_BG;
  readonly vpsPlanImageUrl = VPS_PLAN_IMAGE_URL;

  private readonly comparisonData: VpsComparisonRow[] = [
    { section: 'CARACTERISTICAS GENERALES' },
    {
      feature: 'Espacio en el Disco',
      helpText: 'Espacio en el disco duro disponible en tu VPS',
      cells: ['20 gB', '50 gB', '80 gB', '160 gB', '320 gB', '640 gB', '1280 gB']
    },
    {
      feature: 'Transferencia Mensual',
      helpText: 'Es la cantidad de información transferida desde o hacia el sitio web o servidor',
      cells: ['1000 gB', '2000 gB', '3000 gB', '4000 gB', '5000 gB', '6000 gB', '7000 gB']
    },
    {
      feature: 'RAM Garantizado',
      helpText: 'Mejora el rendimiento de tu VPS',
      cells: ['1 gB', '2 gB', '4 gB', '8 gB', '16 gB', '32 gB', '64 gB']
    },
    {
      feature: 'CPU - Cores',
      helpText: 'Unidad central de procesamiento que interpreta las instrucciones contenidas en los programas y procesa los datos',
      cells: ['1', '1', '2', '4', '6', '8', '16']
    },
    {
      feature: 'Direcciones IP',
      helpText: 'Internet Protocol',
      cells: ['1', '1', '1', '1', '1', '1', '1']
    },
    {
      feature: 'Uptime',
      helpText: 'Es la medida de tiempo en que una máquina o equipo ha estado funcionando sin interrupción de alguna clase',
      cells: ['99,9%', '99,9%', '99,9%', '99,9%', '99,9%', '99,9%', '99,99%']
    },
    { section: 'PANEL DE CONTROL' },
    {
      feature: 'WHM/cPanel',
      helpText: 'Paneles donde podrás administrar de manera fácil tu cuenta',
      cells: ['Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional']
    },
    {
      feature: 'DirectAdmin License',
      helpText: 'Licencia de Panel de Control DirectAdmin, servicio adicional pagado',
      cells: ['Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional', 'Pago Adicional']
    },
    { section: 'CARACTERISTICAS GENERALES SERVIDORES' },
    {
      feature: 'Discos Duros',
      helpText: 'Tipo de disco duro',
      cells: [
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante',
        'SSD SATA III Raid 10 Array - Redundante'
      ]
    },
    {
      feature: 'Procesadores',
      helpText: 'Tipo de procesador',
      cells: [
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)',
        'Dual Quad Core Xeon (8 Procesadores Físicos)'
      ]
    },
    {
      feature: 'Xen Technology',
      helpText: 'Sistema de virtualización',
      cells: ['check', 'check', 'check', 'check', 'check', 'check', 'check']
    },
    {
      feature: 'Kernel Personalizado',
      helpText: 'Cargar tu propio sistema operativo personalizado',
      cells: ['check', 'check', 'check', 'check', 'check', 'check', 'check']
    },
    {
      feature: 'PV-GRUB Personalizado',
      helpText: 'Le permite tener un Kernel Personalizado',
      cells: ['check', 'check', 'check', 'check', 'check', 'check', 'check']
    },
    { section: 'OTRAS CARACTERISTICAS' },
    {
      feature: 'Soporta Multiples Linux Distro',
      helpText: '',
      cells: ['check', 'check', 'check', 'check', 'check', 'check', 'check']
    },
    {
      feature: 'Soporta Multiples Kernel',
      helpText: '',
      cells: ['check', 'check', 'check', 'check', 'check', 'check', 'check']
    }
  ];

  constructor(
    private readonly locale: RackeoLocaleService,
    public readonly cart: RackeoCartService,
    private readonly meta: MetaService,
    private readonly canonical: CanonicalService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    this.comparisonRows = this.comparisonData;
    const country = RACKEO_SITE.countryName;
    this.meta.setTitle(`VPS ${country} | Virtualización Linux Cloud SSD | Rackeo`);
    this.meta.setTag('description', `Servidores VPS Cloud SSD con RAM garantizado, disco SSD, Xen Technology y uptime 99,9%. Los precios más bajos del mercado en ${country}.`);
    this.meta.setTag('og:type', 'website');
    this.meta.setTag('og:image:type', 'image/png');
    this.meta.setTag('og:image', RACKEO_SITE.ogImageUrl);
    this.meta.setTag('og:image:width', '1200');
    this.meta.setTag('og:image:height', '1200');
    this.meta.setTag('og:image:alt', `VPS ${country} | Virtualización Linux Cloud SSD | Rackeo`);
    this.meta.setTag('og:locale', RACKEO_SITE.ogLocale);
    this.canonical.set('/virtualizacion');
    this.locale.getContent().pipe(take(1)).subscribe((content) => {
      if (content) {
        this.applyContent(content);
      }
    });
  }

  selectPlan(index: number): void {
    if (index >= 0 && index < this.plans.length) {
      this.selectedPlanIndex = index;
    }
  }

  setTab(index: number): void {
    this.activeTab = index;
  }

  tabAccent(i: number): string {
    return PLAN_TAB_ACCENTS[i] ?? PLAN_TAB_ACCENTS[0];
  }

  ramParts(plan: RackeoVirtualizacionPlan): { gb: string; ram: string } {
    const s = plan.subtitle.trim();
    const ramLabel = 'RAM';
    if (!s || s.length <= ramLabel.length) {
      return { gb: s, ram: '' };
    }

    const suffix = s.slice(-ramLabel.length);
    if (suffix.toUpperCase() !== ramLabel) {
      return { gb: s, ram: '' };
    }

    const gbWithWhitespace = s.slice(0, -ramLabel.length);
    const gb = gbWithWhitespace.trimEnd();
    if (gb.length === gbWithWhitespace.length || !gb) {
      return { gb: s, ram: '' };
    }

    return { gb, ram: suffix };
  }

  diskSubtitle(plan: RackeoVirtualizacionPlan): string {
    const d = plan.features?.[0]?.value ?? '';
    return d ? `Disco SSD ${d}` : '';
  }

  priceStripBg(planIndex: number, rowIndex: number): string {
    const row = PRICE_STRIP_COLORS[planIndex] ?? PRICE_STRIP_COLORS[0];
    return row[rowIndex] ?? row[0];
  }

  private applyContent(content: RackeoContent): void {
    const v = content.virtualizacion;
    const promo = content.menuPromo;

    this.applyCatalog(v);

    this.heroSrc = v?.heroImage?.trim() ? v.heroImage.trim() : HERO_DEFAULT_SRC;
    this.heroAlt = v?.heroAlt?.trim() ? v.heroAlt.trim() : HERO_DEFAULT_ALT;

    this.addOns = v?.addOns?.length ? v.addOns : [];

    if (v?.plans?.length === 7 && v.plans.every((p) => (p.contracts?.length ?? 0) > 0)) {
      this.plans = v.plans;
    } else {
      this.plans = this.buildPlansFromMenuPromo(promo);
    }

    if (v?.comparisonPriceRows?.length) {
      this.comparisonPriceRows = v.comparisonPriceRows;
    } else {
      this.comparisonPriceRows = this.buildPriceRowsFromPlans(this.plans);
    }

    this.selectedPlanIndex = Math.min(this.selectedPlanIndex, Math.max(0, this.plans.length - 1));
  }

  private applyCatalog(v: RackeoVirtualizacionData | undefined): void {
    this.vpsPids = v?.pids?.length === 7 ? v.pids : [];
    this.ramLabels = v?.ramLabels?.length === 7 ? v.ramLabels : [];
    this.specs = v?.specs?.length === 7 ? v.specs : [];
    this.fallbackMonthlyPrices = v?.fallbackMonthlyPrices?.length === 7 ? v.fallbackMonthlyPrices : [];
  }

  private buildPlansFromMenuPromo(promo: RackeoMenuPromoData | undefined): RackeoVirtualizacionPlan[] {
    const rows = promo?.vpsPlans;
    if (rows?.length !== 7) {
      return this.buildPlansFromMonthlyFallback();
    }
    return rows.map((row, i) => this.planFromVpsPriceRow(i, row));
  }

  private buildPlansFromMonthlyFallback(): RackeoVirtualizacionPlan[] {
    return this.fallbackMonthlyPrices.map((monthly, i) => {
      const row: RackeoMenuVpsPriceRow = {
        label: `SSD${i + 1}`,
        mo: monthly,
        tri: monthly * 3,
        sem: monthly * 6,
        an: Math.round(monthly * 12 * 0.9),
        bi: Math.round(monthly * 24 * 0.85),
        triAn: Math.round(monthly * 36 * 0.8)
      };
      return this.planFromVpsPriceRow(i, row);
    });
  }

  private planFromVpsPriceRow(index: number, row: RackeoMenuVpsPriceRow): RackeoVirtualizacionPlan {
    const spec = this.specs[index];
    const contracts: RackeoVirtualizacionContract[] = [
      { label: '1 Mes', monthlyPrice: row.mo },
      { label: '1 año', monthlyPrice: this.monthlyEquivFromPeriodTotal(row.an, 12) },
      { label: '2 años', monthlyPrice: this.monthlyEquivFromPeriodTotal(row.bi, 24) },
      { label: '3 años', monthlyPrice: this.monthlyEquivFromPeriodTotal(row.triAn, 36) }
    ];
    return {
      id: `ssd${index + 1}`,
      pid: this.vpsPids[index],
      name: `SSD ${index + 1}`,
      subtitle: this.ramLabels[index] ?? '',
      features: spec ? [
        { label: 'Espacio Disco SSD', value: spec.disk },
        { label: 'Transferencia Mensual', value: spec.transfer },
        { label: 'RAM Garantizado', value: spec.ram },
        { label: 'CPU - Cores', value: spec.cpu },
        { label: 'Direcciones IP', value: '1' },
        { label: 'Up Time', value: spec.uptime }
      ] : [],
      contracts,
      colorClass: `plan-vps${index + 1}`
    };
  }

  private monthlyEquivFromPeriodTotal(total: number, monthsInPeriod: number): number {
    if (monthsInPeriod <= 0) {
      return 0;
    }
    return Math.round(total / monthsInPeriod);
  }

  private buildPriceRowsFromPlans(plans: RackeoVirtualizacionPlan[]): RackeoVirtualizacionPriceRow[] {
    if (!plans.length) {
      return [];
    }
    const firstContracts = plans[0].contracts;
    if (!firstContracts?.length) {
      return [];
    }
    const rows: RackeoVirtualizacionPriceRow[] = [];
    for (let c = 0; c < firstContracts.length; c++) {
      const short = firstContracts[c].label;
      const values = plans.map((p) => p.contracts?.[c]?.monthlyPrice ?? 0);
      rows.push({ label: this.comparisonPriceLabel(short), values });
    }
    return rows;
  }

  private comparisonPriceLabel(short: string): string {
    switch (short) {
      case '1 Mes':
        return 'Contrato por 1 Mes';
      case '1 año':
        return 'Contrato por 1 Año';
      case '2 años':
        return 'Contrato por 2 Años';
      case '3 años':
        return 'Contrato por 3 Años';
      default:
        return `Contrato por ${short}`;
    }
  }

  scrollToComoComprar(event: Event): void {
    event.preventDefault();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    scrollToElementById('como_comprar');
  }

  comparisonColspan(): number {
    return 2 + (this.plans.length || 7);
  }

}
